import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import MODALS from '@constants/modals';
import { useEntityStore } from '@ContractBuilder/store';
import { useMultiSelectStore } from '@ContractBuilder/store/multi-select.store';
import { BULK_DELETE_BLOCKS_MAX } from '@domain/schemas/payload.schema';
import { canChange } from '@helpers/canChange';
import { useCurrentContractSections } from '@hooks/useCurrentContractSections';
import { useModalDispatch } from '@hooks/useModalProvider';

import { MultiSelectPanel } from '../views/MultiSelectPanel';

export const MultiSelectPanelController: FC = () => {
  const sections = useCurrentContractSections();

  const activePartId = useMultiSelectStore((state) => state.activePartId);
  const selectedCount = useMultiSelectStore((state) => state.selectedBlockIds.size);
  const selectAllInPart = useMultiSelectStore((state) => state.selectAllInPart);
  const clearSelection = useMultiSelectStore((state) => state.clearSelection);
  const { submissionStatus, bulkDeleteBlocks } = useEntityStore((state) => ({
    submissionStatus: state.submission?.status,
    bulkDeleteBlocks: state.bulkDeleteBlocks,
  }));

  const [isDeleting, setIsDeleting] = useState(false);

  const { showModal } = useModalDispatch();

  const activeSection = useMemo(() => {
    if (!activePartId) {
      return undefined;
    }
    return sections.find(({ id }) => id === activePartId);
  }, [activePartId, sections]);

  const partBlockIds = useMemo(() => activeSection?.blocks.map(({ id }) => id) ?? [], [activeSection]);
  const totalCount = partBlockIds.length;
  const isOpen = Boolean(activePartId) && selectedCount > 0;
  const effectiveMax = Math.min(totalCount, BULK_DELETE_BLOCKS_MAX);
  const exceedsBulkLimit = totalCount > BULK_DELETE_BLOCKS_MAX;

  const handleToggleSelectAll = useCallback(() => {
    if (!activePartId) {
      return;
    }
    if (selectedCount === effectiveMax && effectiveMax > 0) {
      clearSelection();
      return;
    }
    selectAllInPart(activePartId, partBlockIds.slice(0, BULK_DELETE_BLOCKS_MAX));
    if (exceedsBulkLimit) {
      toast.info(`You can remove up to ${BULK_DELETE_BLOCKS_MAX} blocks at a time.`);
    }
  }, [activePartId, clearSelection, effectiveMax, exceedsBulkLimit, partBlockIds, selectAllInPart, selectedCount]);

  const handleRemove = useCallback(() => {
    if (!activePartId) {
      return;
    }
    const blockIds = [...useMultiSelectStore.getState().selectedBlockIds];
    if (blockIds.length === 0) {
      return;
    }
    const firstBlock = activeSection?.blocks.find(({ id }) => id === blockIds[0]);
    const removalCount = blockIds.length;
    showModal(MODALS.CONFIRM_BULK_DELETE_BLOCKS, {
      blockIds,
      firstBlockName: firstBlock?.name,
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          return await bulkDeleteBlocks(activePartId, blockIds);
        } finally {
          setIsDeleting(false);
        }
      },
      onConfirmSuccess: () => toast.success(`Removed ${removalCount} ${removalCount === 1 ? 'block' : 'blocks'}`),
    });
  }, [activePartId, activeSection, bulkDeleteBlocks, showModal]);

  if (!canChange(submissionStatus)) {
    return null;
  }

  return (
    <MultiSelectPanel
      isOpen={isOpen}
      selectedCount={selectedCount}
      totalCount={totalCount}
      isSubmitting={isDeleting}
      onToggleSelectAll={handleToggleSelectAll}
      onRemove={handleRemove}
    />
  );
};
